import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { directory, tasks } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { personDirectory, type PersonDirectory } from '../personDirectory.js';
import { PersonChip } from '../PersonChip.js';
import { CompletionStatement } from './CompletionStatement.js';
import {
  ORIGIN_LABEL,
  PRIORITY_LABEL,
  PRIORITY_VARIANT,
  STATUS_LABEL,
  STATUS_VARIANT,
  dueSentence,
  escalationSentence,
  modeLabel,
  taskProse,
  taskTitle,
} from './taskLogic.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * One task: what is owed, who owes it, what has been claimed, what has been
 * observed, and the immutable history of all of it.
 *
 * READ-ONLY, AND THAT IS A DESIGN DECISION RATHER THAN AN UNFINISHED SCREEN.
 * The writes this domain offers are assign, attest, observe, reopen and close.
 * Attestation puts a person's name on a claim about work they did; observation
 * is Phibian's own act and has no human author at all. Neither belongs behind a
 * button an administrator presses while reading somebody else's queue, and the
 * observation route in particular must never be reachable from a screen — a
 * person pressing "record observation" would be a person authoring what the
 * platform saw. What a reader needs first is to be able to SEE the obligation
 * and tell a claim from a verification, which is what this screen is.
 *
 * THE HISTORY IS SHOWN IN FULL, oldest first, with the actor kind on every
 * entry. `actorKind: 'phibian'` and `actorKind: 'user'` are drawn differently
 * because "the platform observed this" and "a person did this" are the two
 * things this domain exists to keep apart, and a history that renders them
 * identically re-merges them at the last possible moment.
 */
export function TaskDetail() {
  const navigate = useNav();
  const { taskId } = useParams({ strict: false }) as { taskId: string };
  const permissions = usePermissions();
  /** The same question as `TasksQueue`, for the same reason — see its docblock. */
  const canReadDirectory = mayListWorkers(permissions);

  const detail = useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: () => tasks.detail(taskId),
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-tasks'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });

  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );

  return (
    <div className="rn-col" style={{ maxWidth: 820 }}>
      <button type="button" className="rn-link" onClick={() => navigate('/tasks')}>
        ← Tasks
      </button>

      {detail.isLoading ? (
        <SkeletonSet surface="drawer" rows={8} />
      ) : detail.isError && !detail.data ? (
        <LoadFailure
          error={detail.error}
          message="We couldn’t load this task just now. It has not been closed or completed by this failure."
          onRetry={() => void detail.refetch()}
          mainRead
        />
      ) : detail.data ? (
        <TaskView detail={detail.data} people={people} />
      ) : null}
    </div>
  );
}

function TaskView({ detail, people }: { detail: tasks.TaskDetail; people: PersonDirectory }) {
  const { task } = detail;
  const now = new Date();

  return (
    <article className="rn-col">
      <header className="rn-col">
        <h1>{taskTitle(task)}</h1>
        <div className="rn-row">
          <Pill variant={STATUS_VARIANT[task.status]}>{STATUS_LABEL[task.status]}</Pill>
          <Pill variant={PRIORITY_VARIANT[task.priority]}>{PRIORITY_LABEL[task.priority]}</Pill>
          <Pill variant="plain">{ORIGIN_LABEL[task.origin]}</Pill>
        </div>
        {(() => {
          // THREE STATES, NOT TWO. `task.description ? ... : null` printed
          // nothing for an unwritten description AND nothing for a withheld
          // one, which told this reader the two were the same thing.
          const prose = taskProse(task.description, task);
          if (!prose) return null;
          return <p className={prose.withheld ? 'rn-muted' : undefined}>{prose.text}</p>;
        })()}
      </header>

      {/* The completion, in the one place that is allowed to say what it means. */}
      <section className="rn-col" aria-label="Completion">
        <h2>What is known</h2>
        <CompletionStatement task={task} people={people} showStatement />
      </section>

      <section className="rn-col" aria-label="The obligation">
        <h2>The obligation</h2>
        <dl className="rn-facts">
          <dt>Underlying condition</dt>
          <dd>{taskProse(task.underlyingCondition, task)?.text ?? '—'}</dd>

          <dt>How it completes</dt>
          <dd>
            {modeLabel(task.verificationMode)}
            <br />
            <span className="rn-muted">
              {tasks.VERIFICATION_MODE_EXPLANATION[task.verificationMode]}
            </span>
          </dd>

          <dt>What Phibian watches</dt>
          <dd>
            {task.observableConditionKey ?? (
              // The honest rendering of a null. `human_attested` means there is
              // no observable signal, not that we forgot to record one.
              <span className="rn-muted">
                Nothing. Phibian cannot observe this outcome, which is what “person attests” means.
              </span>
            )}
          </dd>

          <dt>Responsible</dt>
          <dd>
            <PersonChip
              person={people.person(task.responsibleUserId ?? task.assigneeUserId)}
              detail={`routed to the ${tasks.ROUTING_TARGET_LABEL[task.assigneeKind]}`}
            />
          </dd>

          <dt>Due</dt>
          <dd>{dueSentence(task, now)}</dd>

          <dt>Escalation</dt>
          <dd>{escalationSentence(task)}</dd>

          <dt>Reopened</dt>
          <dd>{task.reopenCount === 0 ? 'Never' : `${task.reopenCount} time(s)`}</dd>
        </dl>
      </section>

      <section className="rn-col" aria-label="What Phibian observed">
        <h2>What Phibian observed</h2>
        {detail.observations.length === 0 ? (
          <p className="rn-muted">
            {task.verificationMode === 'human_attested'
              ? 'Nothing, and nothing ever will be. This task’s outcome is not something Phibian can watch.'
              : 'Nothing yet. Phibian has not reported on this condition.'}
          </p>
        ) : (
          <ul className="rn-list">
            {detail.observations.map((observation) => (
              <li key={observation.id}>
                <strong>{OBSERVED_STATE_LABEL[observation.observedState]}</strong>{' '}
                <span className="rn-muted">
                  {observation.conditionKey} · {formatWhen(observation.observedAt)}
                </span>
                {observation.evidenceNote ? <p>{observation.evidenceNote}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rn-col" aria-label="History">
        <h2>History</h2>
        <p className="rn-muted">
          Oldest first. This record cannot be edited — not by an administrator, not by the person
          responsible, not by Phibian.
        </p>
        <ol className="rn-list">
          {detail.history.map((event) => (
            <li key={event.id} data-actor={event.actorKind}>
              <strong>{EVENT_LABEL[event.kind]}</strong>{' '}
              <span className="rn-muted">
                {event.actorKind === 'phibian'
                  ? '· Phibian'
                  : event.actorKind === 'system'
                    ? '· the platform'
                    : `· ${people.name(event.actorUserId)}`}{' '}
                · {formatWhen(event.occurredAt)}
              </span>
              {event.detail ? <p>{event.detail}</p> : null}
            </li>
          ))}
        </ol>
      </section>
    </article>
  );
}

const OBSERVED_STATE_LABEL: Readonly<Record<tasks.ObservedState, string>> = Object.freeze({
  resolved: 'Phibian saw the condition resolve',
  // Recorded as evidence, and it leaves a standing claim standing but
  // unverified — the honest state hybrid exists for.
  unresolved: 'Phibian looked and the condition had not resolved',
  indeterminate: 'Phibian looked and could not tell',
});

/*
 * TOTAL OVER `TaskEventKind`, INCLUDING THE KINDS NOTHING WRITES.
 *
 * `reminded`, `due_changed` and `completion_claimed` are RESERVED AND NOT
 * PERFORMED — `insertEvent`'s callers do not write one (see
 * `RESERVED_UNPERFORMED_TASK_EVENT_KINDS` in the tasks vocabulary). They keep
 * their labels because a history row this map has no entry for renders
 * `undefined` in a timeline, and because the history is READ here rather than
 * offered: nothing on this screen invites a reader to expect a reminder.
 *
 * `escalated` USED TO BE IN THAT LIST AND IS NOW WRITTEN — the escalation sweep
 * (`tasks/service.runTaskEscalations`) inserts one with `actor_kind = 'system'`
 * whenever a task passes its policy's clock. Its label was already here, which
 * is the whole argument for keeping a total map: the day the platform started
 * producing the fact, the timeline rendered it correctly with no change at all.
 *
 * A REPEAT ESCALATION CARRIES NO STATUS PAIR. `task_event_status_ck` refuses
 * `from_status = to_status`, so the second raising of an already-escalated task
 * is recorded with both bounds null — the obligation did not move. A timeline
 * that printed an arrow between two statuses here would be inventing one.
 */
const EVENT_LABEL: Readonly<Record<tasks.TaskEventKind, string>> = Object.freeze({
  created: 'Raised',
  assigned: 'Assigned',
  reassigned: 'Reassigned',
  due_changed: 'Due date changed',
  reminded: 'Reminder sent',
  escalated: 'Escalated',
  attested: 'A person attested',
  completion_claimed: 'Marked done by a person',
  observed: 'Phibian recorded an observation',
  verified: 'Phibian verified',
  reopened: 'Reopened',
  closed: 'Closed',
});

function formatWhen(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'time unreadable';
  return when.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
